import { Vector3 } from 'three';
import { PlacementStrategy, SupportFoot, SupportResult, TrackSupportPrimitive } from './types';

type V3 = [number, number, number];

function toV3(v: Vector3): V3 {
  return [v.x, v.y, v.z];
}

function fromV3(v: V3): Vector3 {
  return new Vector3(v[0], v[1], v[2]);
}

/** JSON-safe support primitives for worker ↔ main transfer. */
export type SerializedPrimitive =
  | { kind: 'footer'; pos: V3; radius: number; height: number }
  | { kind: 'column'; a: V3; b: V3; radius: number; strategy: PlacementStrategy }
  | { kind: 'brace'; a: V3; b: V3; radius: number }
  | { kind: 'knee'; a: V3; b: V3; radius: number }
  | { kind: 'stub'; a: V3; b: V3; radiusA: number; radiusB: number; attachS?: number }
  | { kind: 'joint'; pos: V3; radius: number };

export type SerializedFoot = {
  attachS: number;
  role: 'main' | 'triangle';
  pos: V3;
};

export interface SerializedSupportResult {
  primitives: SerializedPrimitive[];
  feet: SerializedFoot[];
  skipped: number[];
  warnings: string[];
}

export function serializeSupportResult(result: SupportResult): SerializedSupportResult {
  return {
    skipped: result.skipped.slice(),
    warnings: result.warnings.slice(),
    feet: result.feet.map(
      (f): SerializedFoot => ({ attachS: f.attachS, role: f.role, pos: toV3(f.pos) }),
    ),
    primitives: result.primitives.map((p): SerializedPrimitive => {
      switch (p.kind) {
        case 'footer':
          return { kind: 'footer', pos: toV3(p.pos), radius: p.radius, height: p.height };
        case 'column':
          return {
            kind: 'column',
            a: toV3(p.a),
            b: toV3(p.b),
            radius: p.radius,
            strategy: p.strategy,
          };
        case 'brace':
          return { kind: 'brace', a: toV3(p.a), b: toV3(p.b), radius: p.radius };
        case 'knee':
          return { kind: 'knee', a: toV3(p.a), b: toV3(p.b), radius: p.radius };
        case 'stub':
          return {
            kind: 'stub',
            a: toV3(p.a),
            b: toV3(p.b),
            radiusA: p.radiusA,
            radiusB: p.radiusB,
            attachS: p.attachS,
          };
        case 'joint':
          return { kind: 'joint', pos: toV3(p.pos), radius: p.radius };
        default: {
          const _exhaustive: never = p;
          return _exhaustive;
        }
      }
    }),
  };
}

export function deserializeSupportResult(raw: SerializedSupportResult): SupportResult {
  return {
    skipped: raw.skipped.slice(),
    warnings: raw.warnings.slice(),
    feet: (raw.feet ?? []).map(
      (f): SupportFoot => ({ attachS: f.attachS, role: f.role, pos: fromV3(f.pos) }),
    ),
    primitives: raw.primitives.map((p): TrackSupportPrimitive => {
      switch (p.kind) {
        case 'footer':
          return { kind: 'footer', pos: fromV3(p.pos), radius: p.radius, height: p.height };
        case 'column':
          return {
            kind: 'column',
            a: fromV3(p.a),
            b: fromV3(p.b),
            radius: p.radius,
            strategy: p.strategy,
          };
        case 'brace':
          return { kind: 'brace', a: fromV3(p.a), b: fromV3(p.b), radius: p.radius };
        case 'knee':
          return { kind: 'knee', a: fromV3(p.a), b: fromV3(p.b), radius: p.radius };
        case 'stub':
          return {
            kind: 'stub',
            a: fromV3(p.a),
            b: fromV3(p.b),
            radiusA: p.radiusA,
            radiusB: p.radiusB,
            attachS: p.attachS,
          };
        case 'joint':
          return { kind: 'joint', pos: fromV3(p.pos), radius: p.radius };
        default: {
          const _exhaustive: never = p;
          return _exhaustive;
        }
      }
    }),
  };
}
